import {emitOnce, filterNil, Store} from "@ngneat/elf";
import {deleteEntities, deleteEntitiesByPredicate, selectMany, updateEntities, upsertEntities} from "@ngneat/elf-entities";
import {getManyByPredicate} from "felles-ng2/tjenester/datalager/getManyByPredicate";
import {HttpErrorResponse, HttpResponse} from "@angular/common/http";
import {DataOgMeta, dataOgMetaForCachetSvar, dataOgMetaForFeiletRequest, dataOgMetaForOkRequest} from "felles-ng2/tjenester/datalager/DataOgMeta";
import {navnPåEnuminnslag} from "felles-ng2/støttefunksjoner/enumer";
import {asapScheduler, BehaviorSubject, catchError, EMPTY, filter, finalize, merge, MonoTypeOperatorFunction, Observable, observeOn, of, switchMap, throwError} from "rxjs";
import {map, tap} from "rxjs/operators";
import {RequestRespons} from "felles-ng2/tjenester/ressurser/AngularHttpKlient";
import {Debugger} from "debug";
import {StoreEvent} from "@ngneat/elf/src/lib/events";
import {Merge} from "@ngneat/elf/src/lib/state";
import {erIkkeTomListe} from "felles-ng2/støttefunksjoner/lister";
import {erIkkeTomStreng, erStreng} from "felles-ng2/støttefunksjoner/strenger";
import {MeldingTilBrukerNg2} from "felles-ng2/tjenester/MeldingTilBrukerNg2";
import {verifiserErSann, verifiserErSannVedDevMode} from "felles-ng2/støttefunksjoner/antakelser";
import {EntitetId} from "felles-ng2/tjenester/ressurser/ng2HttpKlientTilleggstyper";
import {erIkkeNil, erNil} from "felles-ng2/støttefunksjoner/nilsjekk";
import {utelatFørsteDersomNil} from "felles/ng2/rxjsHjelpere";
import {distinctUntilChangedDeep} from "felles-ng2/støttefunksjoner/observable";
import {RestResponse} from "felles-ng2/tjenester/ressurser/generertNg2HttpKlient";
import {DatalagerAbonnenter} from "felles-ng2/tjenester/datalager/DatalagerAbonnenter";
import {HttpResponskoder} from "felles-ng2/infrastruktur/http/httpResponskoder";

/**********************************************************************
 * hjelpemetoder for operasjoner mot Elf-basert lokalt data-lager
 **********************************************************************/

/** Entitet med id-property der feltet som representerer id er konfigurerbart med 'id' som standard.
 * Eks 'const enEntitet: EntitetMedId<'enAnnenId'> = { enAnnenId: 'enIdVerdi', ..øvrige felter.. }'*/
export type EntitetMedId<IdFelt extends string = 'id', IdVerdi = string> = { [P in IdFelt]: IdVerdi; }

/** Lett forenklet typedefinisjon av Elf Store */
export type EntitetLager<Entitet extends EntitetMedId<IdFelt>, IdFelt extends string = 'id'> = Store<{
    name: string;
    state: Merge<[{ props: { entities: Record<Entitet[IdFelt], Entitet>; ids: Entitet[IdFelt][] }; }], "props">;
    config: Merge<[{ config: { idKey: IdFelt } }], "config">
}>;

/** Sann dersom angitt lager inneholder entitet med angitt id */
export function finnesILokallager<E extends EntitetMedId<IdFelt>, IdFelt extends string>(lager: EntitetLager<E, IdFelt>, id: E[IdFelt]): boolean {
    return erIkkeNil(lager.value.entities[id])
}

/** Returnerer entitet fra lokallager.
 * Kaster exception dersom entitet ikke er registrert i lokallager */
export function hentFraLokallager<E extends EntitetMedId<IdFelt>, IdFelt extends string>(lager: EntitetLager<E, IdFelt>, id: E[IdFelt]): E {
    verifiserErSann(finnesILokallager(lager, id), `finner ikke entitet med id ${String(id)} i lokallager`)
    return lager.value.entities[id];
}

/** Returnerer entitet fra lokallager.
 * Returnerer null dersom entitet ikke er registrert i lokallager */
export function hentFraLokallagerEllerNull<E extends EntitetMedId<IdFelt>, IdFelt extends string>(lager: EntitetLager<E, IdFelt>, id: E[IdFelt]): E | null {
    return lager.value.entities[id] ?? null;
}

/**
 * Registrer ny eller oppdatert entitet
 */
export function registrerEntitet<LAGER_TYPE extends EntitetMedId<IdFelt>, E extends LAGER_TYPE, IdFelt extends string>(lager: EntitetLager<LAGER_TYPE, IdFelt>, nyEntitet: E): E {
    lager.update(upsertEntities(nyEntitet));
    return nyEntitet;
}

/**
 * Registrer ny eller oppdatert entitet, setter angitt forelder
 */
export function registrerEntitetMedForelder<LAGER_TYPE extends EntitetMedId<IdFelt>, E extends LAGER_TYPE, IdFelt extends string>(lager: EntitetLager<LAGER_TYPE, IdFelt>, nyEntitet: E, forelderId: ForelderId<LAGER_TYPE>): E & MedForelderId {
    const entitetMedForelder = leggTilForelderId(nyEntitet, somForelderFn(forelderId)(nyEntitet));
    lager.update(upsertEntities(entitetMedForelder));
    return entitetMedForelder;
}

/**
 * Registrer eller oppdater alle angitt entiteter.
 * Rekkefølgen for eksisterende entiteter blir ikke påvirket, ny entiteter legges til etter eksisterende,
 * i den rekkefølge de ligger i angitt `entiteter`
 */
export function registrerEntiteter<LAGER_TYPE extends EntitetMedId<IdFelt>, E extends LAGER_TYPE, IdFelt extends string>(lager: EntitetLager<LAGER_TYPE, IdFelt>, entiteter: E[]): E[] {
    lager.update(upsertEntities(entiteter));
    return entiteter;
}

/**
 * Registrer eller oppdater alle angitt entiteter, setter angitt forelder på disse.
 * Rekkefølgen for eksisterende entiteter blir ikke påvirket, ny entiteter legges til etter eksisterende,
 * i den rekkefølge de ligger i angitt `entiteter`
 */
export function registrerEntiteterMedForelder<LAGER_TYPE extends EntitetMedId<IdFelt>, E extends LAGER_TYPE, IdFelt extends string>(lager: EntitetLager<LAGER_TYPE, IdFelt>, entiteter: E[], forelderId: ForelderId<LAGER_TYPE>): (E & MedForelderId)[] {
    const forelderFn = somForelderFn(forelderId);
    const medForelder = entiteter
      .map(entitet => leggTilForelderId(entitet, forelderFn(entitet)));
    lager.update(upsertEntities(medForelder));
    return medForelder;
}

/**
 * Oppdater entitet og sett angitt forelder
 */
export function oppdaterEntitetMedForelder<LAGER_TYPE extends EntitetMedId<IdFelt>, E extends LAGER_TYPE, IdFelt extends string>(lager: EntitetLager<LAGER_TYPE, IdFelt>, oppdatertEntitet: E, forelderId: ForelderId<LAGER_TYPE>): E & MedForelderId {
    const medForelder = leggTilForelderId(oppdatertEntitet, somForelderFn(forelderId)(oppdatertEntitet));
    lager.update(updateEntities(lesId(oppdatertEntitet, lager), medForelder));
    return medForelder;
}

/**
 * Registrer eller oppdater alle angitt entiteter, setter angitt forelder på disse.
 * Rekkefølgen for eksisterende entiteter blir ikke påvirket, ny entiteter legges til etter eksisterende,
 * i den rekkefølge de ligger i angitt `entiteter`
 */
export function erstatteAlleEntiteterMedForelder<E extends EntitetMedId<IdFelt>, IdFelt extends string>(lager: EntitetLager<E, IdFelt>, entiteter: E[], forelderId: string): (E & MedForelderId)[] {
    const eksisterendeMedForelder = finnFraLokallagerBasertPåForelderId(lager, forelderId)
    const medForelder = entiteter
      .map(entitet => leggTilForelderId(entitet, forelderId));

    //finn entiteter som skal slettes
    const slettedeEntiteter = eksisterendeMedForelder.filter(eksisterende => !entiteter.some(entitet => lesId(entitet, lager) === lesId(eksisterende, lager)));

    emitOnce(() => {
        lager.update(upsertEntities(medForelder));
        lager.update(deleteEntities(slettedeEntiteter.map(e => lesId(e, lager))))
    });
    return medForelder;
}

/** Sletter alle entiteter som har angitt forelderId. */
export function slettAlleEntiteterMedForelder<E extends EntitetMedId<IdFelt>, IdFelt extends string>(lager: EntitetLager<E, IdFelt>, forelderId: string): void {
    lager.update(deleteEntitiesByPredicate(forelderIdPredikat(forelderId)));
}

/** Sletter alle entiteter som har forelderId blant angitte forelderIder. */
export function slettAlleEntiteterMedForeldre<E extends EntitetMedId<IdFelt>, IdFelt extends string>(lager: EntitetLager<E, IdFelt>, forelderIder: string[]): void {
    lager.update(deleteEntitiesByPredicate(foreldreIdPredikat(forelderIder)));
}

/** Sletter angitt entitet og dens barn rekursivt.
 * Returnerer slettede entiteter. */
export function slettRekursivt<E extends EntitetMedId<IdFelt>, IdFelt extends string>(lager: EntitetLager<E, IdFelt>, id: E[IdFelt]): E[] {
    return emitOnce(() => {
        const eksisterende = hentFraLokallagerEllerNull(lager, id);
        if (erNil(eksisterende)) return [];
        lager.update(deleteEntities(id));
        //slett barn rekursivt
        const slettedeBarn = finnFraLokallagerBasertPåForelderId(lager, id as string)
          .map(entitet => slettRekursivt(lager, lesId(entitet, lager)))
          .flat()
        return [eksisterende, ...slettedeBarn];
    });
}


export function finnFraLokallagerBasertPåForelderId<E extends EntitetMedId<IdFelt>, IdFelt extends string>(lager: EntitetLager<E, IdFelt>, forelderId: string): E[] {
    return lager.query(getManyByPredicate(forelderIdPredikat(forelderId)));
}

export function forelderIdPredikat(forelderId: string) {
    return (entitet: any) => lesForelderId(entitet) === forelderId;
}

export function foreldreIdPredikat(forelderIder: string[]) {
    return (entitet: any) => forelderIder.includes(lesForelderId(entitet));
}

export function leggTilForelderId<T>(entitet: T, forelderId: string): T & MedForelderId {
    return {...entitet, __forelderId: forelderId};
}

export function lesForelderId<T>(entitet: T): string {
    return (entitet as MedForelderId).__forelderId;
}

export function lesId<E extends EntitetMedId<IdFelt>, IdFelt extends string>(entitet: E, lager: EntitetLager<E, IdFelt>): E[IdFelt] {
    return entitet[lager.getConfig().idKey as IdFelt];
}

export interface MedForelderId {
    __forelderId: string;
}

export interface MedId<ID extends EntitetId = string> {
    id: ID;
}

//private hjelpemetoder

type ForelderFunksjon<E> = (entitet: E) => EntitetId;
type ForelderId<E> = ForelderFunksjon<E> | EntitetId;

function somForelderFn<E>(forelderId: ForelderId<E>): ForelderFunksjon<E> {
    return erStreng(forelderId) ? () => forelderId : forelderId;
}


/**********************************************************************
 * hjelpemetoder for håndtering av eventer fra Elf-basert lokalt data-lager
 **********************************************************************/

/** Returnerer en Observable som leverer når angitt lagere publiserer 'add' og 'update' hendelser.
 * Ved hver hendelse leveres en array med to innslag: hendelsen og entitetene berørt av hendelsen [hendelse, entitet[]].
 * MERK at returnert observable bruker en async schedulere, dermed vil subscribe bli kjørt asynk */
export function opprettEllerOppdatertHendelse<E extends EntitetMedId<IdFelt>, IdFelt extends string>(lager: EntitetLager<E, IdFelt>): Observable<HendelseOgBerørteEntiteter<E>> {
    return lager.events$
      .pipe(
        //med denne vil kode som kjøres i .subscribe(..) kjøres asynkront (som som koden var lagt i setTimeout(<kode>, 0)),
        //dermed unngår vi problem med at nesting av events$ observable fører til at event for forelder blir levert til barn - se datatjeneste.jestspec.ts for eksempel
        observeOn(asapScheduler),
        filtrerHendelser('add', 'update'),
        map(hendelse => ({
            hendelse: hendelse,
            entiteter: hendelse.ids
              //dersom lager.update(updateEntities(...)) kalles med enitetId som ikke finnes i lageret vil det fortsatt lagees en hendelse,
              //sørger her for å filtrere bort slike hendelser (i dev mode kastes exception)
              .filter(entitetId => verifiserErSannVedDevMode(
                finnesILokallager(lager, entitetId),
                () => `${hendelse.type} hendelse referer til ${entitetId} som ikke finnes i lokallager ${lager.name}, har lager.update(updateEntities(...)) blitt kalt med entitetId som ikke finnes i lokallager?`))
              .map(entitetId => hentFraLokallager(lager, entitetId))
        }))
      );
}

/** Returnerer en Observable som leverer når angitt lagere publiserer 'delete' hendelser.
 * MERK at returnert observable bruker en async schedulere, dermed vil subscribe bli kjørt asynk */
export function slettHendelse<E extends EntitetMedId<IdFelt>, IdFelt extends string>(lager: EntitetLager<E, IdFelt>): Observable<StoreEvent> {
    return lager.events$
      .pipe(
        //med denne vil kode som kjøres i .subscribe(..) kjøres asynkront (som om koden var lagt i setTimeout(<kode>, 0)),
        //dermed unngår vi problem med at nesting av events$ observable fører til at event for forelder blir levert til barn - se datatjeneste.jestspec.ts for eksempel
        observeOn(asapScheduler),
        filtrerHendelser('delete')
      );
}

export type HendelseOgBerørteEntiteter<E> = { hendelse: StoreEvent; entiteter: E[]; }

function filtrerHendelser<T extends StoreEvent>(...typer: StoreEvent['type'][]): MonoTypeOperatorFunction<T> {
    return filter(hendelse =>
      typer.includes(hendelse.type)
      && erIkkeTomListe(hendelse.ids)) //bryr oss ikke om hendelser uten berørte entiteter
}

/**********************************************************************
 * hjelpemetoder for håndtering av http-requester og Elf-basert lokalt data-lager
 **********************************************************************/


/**
 * Returnerer en strøm av data fra lokallager/cache samt henter ferske data fra server, pakket inn i {@link DataOgMeta},
 * registrerer også returnerte entiteter i angitt abonnentRegister.
 *
 * Strømmen vil få følgende leveranser:
 * - umiddelbart fra lokallager/cache (via {@link KjørRequestOgOppdaterLagerArgumenter#hentFraLager})
 * - data hentet fra server (via {@link KjørRequestOgOppdaterLagerArgumenter#ressurskall})
 * - når andre metoder oppdaterer data i lokallager/cache (via {@link KjørRequestOgOppdaterLagerArgumenter#hentFraLager})
 */
export function serverFraLagerOgRequestPakketIMetaRegistrerAbonnenterPåEntitetIder<E extends EntitetMedId<'id'>>(argumenter: KjørRequestOgOppdaterLagerRegistrerAbonnenterPåEntitetIderArgumenter<E>): Observable<DataOgMeta<E[]>> {
    //liste med ider for alle entiteter som ble returnert av ressurskall
    const iderStrøm = new BehaviorSubject<string[]>([]);
    return serverFraLagerOgRequestPakketIMeta({
        ressurskall: () => argumenter.ressurskall(),
        registrerILager: (respons: HttpResponse<E[]>) => {
            const entiteter = respons.body!;
            const ider = entiteter.map(entitet => entitet.id)
            iderStrøm.next(ider);
            //registrerer entiteter i abonnentRegister slik at relevante modellendringer vil føre til ny leveranse via hentFraLager-metoden
            argumenter.abonnentRegister.registrer(ider);
            return registrerEntiteterMedForelder(argumenter.lager, entiteter, entitet => argumenter.forelderId(entitet))
        },
        hentFraLager: () => iderStrøm.pipe(
          //lever settet av entiteter fra datalager som tilsvarer idene hentet via ressurskall
          switchMap(ider => argumenter.lager.pipe(selectMany(ider))))
          .pipe(
            argumenter.leveringsvalg == Leveringsvalg.ikke_lever_ved_slettet ? filterNil() : utelatFørsteDersomNil(),
            distinctUntilChangedDeep(),
            finalize(() => argumenter.abonnentRegister.avregistrer(iderStrøm.getValue()))
          ),
        ...argumenter.feilhåndteringArgumenter
    })
}

/**
 * Returnerer en strøm av data fra lokallager/cache samt henter ferske data fra server, pakket inn i {@link DataOgMeta}.
 *
 * Strømmen vil få følgende leveranser:
 * - umiddelbart fra lokallager/cache (via {@link KjørRequestOgOppdaterLagerArgumenter#hentFraLager})
 * - data hentet fra server (via {@link KjørRequestOgOppdaterLagerArgumenter#ressurskall})
 * - når andre metoder oppdaterer data i lokallager/cache (via {@link KjørRequestOgOppdaterLagerArgumenter#hentFraLager})
 */
export function serverFraLagerOgRequestPakketIMeta<REQ_RESP_TYPE, RET_TYPE>(argumenter: KjørRequestOgOppdaterLagerArgumenter<REQ_RESP_TYPE, RET_TYPE, FeilhåndteringsstrategiForDataOgMeta>): Observable<DataOgMeta<RET_TYPE>> {
    argumenter.debug('serverFraLagerOgRequestPakketIMeta kalt', argumenter);
    let ikkeLeverFraCache = false;

    //hent data fra server - når disse er hentet legges de til i cache samt leveres til returnert strøm
    const ferskeData = argumenter.ressurskall().råRespons()
      .pipe(
        map(respons => {
            argumenter.debug('http svar mottatt', respons);
            //lagreIDatalager dytter data inn i datalageret vårt, som i sin tur leverer disse via cachestrøm
            //omsluttet med ikkeLeverFraCache=true for å unngå at verdi mottatt fra server leveres til lytter to ganger - både via ferskeData og cacheData
            ikkeLeverFraCache = true;
            const registrerteData = argumenter.registrerILager(respons)
            ikkeLeverFraCache = false;

            return dataOgMetaForOkRequest(registrerteData, respons)
        }),
        håndterRequestfeilViaCatchErrorForDataOgMeta(argumenter),
        tap(data => argumenter.debug('leveranse fra request', data))
      );

    //hent data fra cache
    const cacheData = argumenter.hentFraLager()
      .pipe(
        map((data): DataOgMeta<RET_TYPE> => {
            argumenter.debug('leveranse fra cache', data);
            return dataOgMetaForCachetSvar(data)
        }),
        //dersom denne leveransen kommer som følge av at http-respons er mottatt dropper vi leveransen
        filter(data => {
            if (ikkeLeverFraCache) argumenter.debug('filtrerer bort leveranser fra cache, venter på ferske data', data);
            return !ikkeLeverFraCache; //false betyr 'filtrer bort'
        })
      );

    return merge(cacheData, ferskeData)
      .pipe(tap(data => argumenter.debug('leverer data', data)))
}

/**
 * Returnerer ferske data fra server, pakket inn i {@link DataOgMeta}.
 * <pre>
 * Strømmen vil få følgende leveranser:
 * - data hentet fra server (via {@link KjørRequestOgOppdaterLagerArgumenter#ressurskall})
 * </pre>
 */
export function serverFraRequestPakketIMeta<RET_TYPE>(argumenter: KjørRequestArgumenter<RET_TYPE, FeilhåndteringsstrategiForDataOgMeta>): Observable<DataOgMeta<RET_TYPE>> {
    return argumenter.ressurskall()
      .pipe(
        map(respons => {
            argumenter.debug('http svar mottatt', respons);
            return dataOgMetaForOkRequest(respons.body!, respons);
        }),
        håndterRequestfeilViaCatchErrorForDataOgMeta(argumenter)
      )
}

/**
 * Returnerer ferske data fra server.
 * <pre>
 * Strømmen vil få følgende leveranser:
 * - data hentet fra server (via {@link KjørRequestOgOppdaterLagerArgumenter#ressurskall})
 * </pre>
 */
export function serverFraRequest<RET_TYPE>(argumenter: KjørRequestArgumenter<RET_TYPE, Feilhåndteringsstrategi>): Observable<RET_TYPE> {
    return argumenter.ressurskall()
      .pipe(
        map(respons => {
            argumenter.debug('http svar mottatt', respons);
            return respons.body!;
        }),
        håndterRequestfeilViaCatchError(argumenter)
      )
}

export interface KjørRequestOgOppdaterLagerRegistrerAbonnenterPåEntitetIderArgumenter<E extends EntitetMedId<'id'>> {
    ressurskall: () => RestResponse<E[]>
    lager: EntitetLager<E, 'id'>;
    abonnentRegister: DatalagerAbonnenter<EntitetId>;
    forelderId: (entitet: E) => EntitetId;
    leveringsvalg: Leveringsvalg;
    feilhåndteringArgumenter: FeilhåndteringArgumenter<any, FeilhåndteringsstrategiForDataOgMeta>;
}

export interface KjørRequestOgOppdaterLagerArgumenter<REQ_RESP_TYPE, RET_TYPE, STRATEGI_TYPE> extends FeilhåndteringArgumenter<RET_TYPE, STRATEGI_TYPE> {
    ressurskall: () => RequestRespons<REQ_RESP_TYPE>;
    registrerILager: (response: HttpResponse<REQ_RESP_TYPE>) => RET_TYPE;
    hentFraLager: () => Observable<RET_TYPE>;
}

export interface KjørRequestArgumenter<RET_TYPE, STRATEGI_TYPE> extends FeilhåndteringArgumenter<RET_TYPE, STRATEGI_TYPE> {
    ressurskall: () => Observable<HttpResponse<RET_TYPE>>;
}


/**********************************************************************
 * hjelpemetoder for pipe
 * Se også hjelpmetoder i {@link DataOgMeta}
 **********************************************************************/

/** Logger mottatte verdier til angitt debug instans.
 * Se felles-ng2/støttefunksjoner/observable.ts#logHendelser for alternativ som logger alle hendelser */
export function logMottatt<T>(debug: Debugger, ekstraMelding = ''): MonoTypeOperatorFunction<T> {
    let teller = 0;
    return tap(mottatt => debug(`mottatt verdi #${++teller}` + erIkkeTomStreng(ekstraMelding) ? `(${ekstraMelding})` : '', mottatt));
}

/**********************************************************************
 * hjelpemetoder for feilhåndtering
 **********************************************************************/

export type STRATEGI_TYPE = FeilhåndteringsstrategiForDataOgMeta | Feilhåndteringsstrategi;

export enum Feilhåndteringsstrategi {
    /** Viser brukerrettet feilmelding via toast-tjeneste, ingen verdi leveres til abonnenten. */
    visFeilmelding,
    /** Feil propageres til kaller, kaller må håndtere feilen via subscribe({error:HttpErrorResponse=>void}) / Promise.error(HttpErrorResponse=>void). */
    propagerSomError,
}

/** Standard feilhåndteringsstrategi dersom ingen er eksplisitt angitt */
export const stdFeilhåndteringsstrategi = Feilhåndteringsstrategi.visFeilmelding


export enum FeilhåndteringsstrategiForDataOgMeta {
    /** Viser brukerrettet feilmelding via toast-tjeneste, ingen verdi leveres til abonnenten. */
    visFeilmelding,
    /** Feil propageres til kaller, kaller må håndtere feilen via subscribe({error:HttpErrorResponse=>void}) / Promise.error(HttpErrorResponse=>void). */
    propagerSomError,
    /** Feilen propagerer til kaller via {@link DataOgMeta#httpResponsStatus}. */
    propagerViaDataOgMeta
}

/** Standard feilhåndteringsstrategi dersom ingen er eksplisitt angitt, viser feilmelding via 'toast' */
export const stdFeilhåndteringsstrategiForDataOgMeta = FeilhåndteringsstrategiForDataOgMeta.visFeilmelding

/** Konverterer fra {@link Feilhåndteringsstrategi} til {@link FeilhåndteringsstrategiForDataOgMeta}*/
export function somFeilhåndteringsstrategiForDataOgMeta(strategi: Feilhåndteringsstrategi): FeilhåndteringsstrategiForDataOgMeta {
    switch (strategi) {
        case Feilhåndteringsstrategi.visFeilmelding:
            return FeilhåndteringsstrategiForDataOgMeta.visFeilmelding;
        case Feilhåndteringsstrategi.propagerSomError:
            return FeilhåndteringsstrategiForDataOgMeta.propagerSomError;
        default:
            console.error(`ukjent feilhåndteringsstrategi: '${navnPåEnuminnslag(strategi, Feilhåndteringsstrategi)}'`)
            return FeilhåndteringsstrategiForDataOgMeta.visFeilmelding;
    }
}

/** Returnerer en Observable som representerer angitt feil, iht angitt feilhåndteringsstrategi.
 * Kaster feil på nytt dersom feilhåndteringsstrategi = propagerSomError,
 * lever DataOgModell-representasjon av feilen dersom feilhåndteringsstrategi = propagerViaDataOgMeta */
export function håndterRequestfeilForDataOgMeta<RET_TYPE>(feil: HttpErrorResponse, argumenter: FeilhåndteringArgumenter<RET_TYPE, FeilhåndteringsstrategiForDataOgMeta>): Observable<DataOgMeta<RET_TYPE>> {
    argumenter.debug(`httpKall feilet med responskode ${feil.status}`, feil);

    // vi har har feil hvor validerToken og hent møte/sak/etc  skjer sammtidig. Vi trenger ikke logge disse kallene som feil eller vise feilmelding, bruker må uansett logge inn på nytt.
    if (feil.status === HttpResponskoder.UNAUTHORIZED) {
        // bruker er ikke autentisert - ikke logg melding, de må logge på.
        return EMPTY;
    }

    switch (argumenter.feilhåndteringsstrategi) {
        case FeilhåndteringsstrategiForDataOgMeta.visFeilmelding:
            argumenter.visFeilmelding()
            return EMPTY;

        case FeilhåndteringsstrategiForDataOgMeta.propagerSomError:
            throw feil;

        case FeilhåndteringsstrategiForDataOgMeta.propagerViaDataOgMeta:
            return of(dataOgMetaForFeiletRequest(feil, argumenter.dataReturnertVedFeil!));

        default:
            console.error(`ukjent feilhåndteringsstrategi: '${navnPåEnuminnslag(argumenter.feilhåndteringsstrategi, FeilhåndteringsstrategiForDataOgMeta)}', httpKall feilet med responskode ${feil.status}`)
            return EMPTY;
    }
}

/** Registrer feilhåndtering i en observable ved å kalle rxjs {@link catchError} med en handler-metode som
 * Kaster feil på nytt dersom feilhåndteringsstrategi = propagerSomError,
 * lever DataOgModell-representasjon av feilen dersom feilhåndteringsstrategi = propagerViaDataOgMeta */
export function håndterRequestfeilViaCatchErrorForDataOgMeta<RET_TYPE>(argumenter: FeilhåndteringArgumenter<RET_TYPE, FeilhåndteringsstrategiForDataOgMeta>) {
    return catchError((feil: HttpErrorResponse, opprinneligObserverbar: Observable<DataOgMeta<RET_TYPE>>): Observable<DataOgMeta<RET_TYPE>> =>
      håndterRequestfeilForDataOgMeta(feil, argumenter));
}

/** Returnerer en Observable som representerer angitt feil, iht angitt feilhåndteringsstrategi.
 * Kaster feil på nytt dersom feilhåndteringsstrategi = propagerSomError,
 * lever DataOgModell-representasjon av feilen dersom feilhåndteringsstrategi = propagerViaDataOgMeta */
export function håndterRequestfeil<RET_TYPE>(feil: HttpErrorResponse, argumenter: FeilhåndteringArgumenter<RET_TYPE, Feilhåndteringsstrategi>): Observable<RET_TYPE> {
    argumenter.debug(`httpKall feilet med responskode ${feil.status}`, feil);
    switch (argumenter.feilhåndteringsstrategi) {
        case Feilhåndteringsstrategi.visFeilmelding:
            argumenter.visFeilmelding()
            return EMPTY; //merk at dette vil føre til exception av typen EmptyErrorImpl i kallere som forventer minst én leveranse (som feks ved bruk av firstValueFrom())

        case Feilhåndteringsstrategi.propagerSomError:
            throw feil;

        default:
            console.error(`ukjent feilhåndteringsstrategi: '${navnPåEnuminnslag(argumenter.feilhåndteringsstrategi, Feilhåndteringsstrategi)}', httpKall feilet med responskode ${feil.status}`)
            return EMPTY;
    }
}

/** Registrer feilhåndtering i en observable ved å kalle rxjs {@link catchError} men en handler-metode som
 * kaller {@link throwError} dersom feilhåndteringsstrategi = propagerSomError */
export function håndterRequestfeilViaCatchError<RET_TYPE>(argumenter: FeilhåndteringArgumenter<RET_TYPE, Feilhåndteringsstrategi>) {
    return catchError((feil: HttpErrorResponse, opprinneligObserverbar: Observable<RET_TYPE>): Observable<RET_TYPE> =>
      håndterRequestfeil(feil, argumenter));
}

type visFeilmeldingFunk = () => void;

export interface FeilhåndteringArgumenter<RET_TYPE, STRATEGI_TYPE> {
    visFeilmelding: visFeilmeldingFunk;
    feilhåndteringsstrategi: STRATEGI_TYPE;
    dataReturnertVedFeil: RET_TYPE | null;
    debug: (formatter: any, ...args: any[]) => void;
}

/** Viser feilmelding vhja {@link MeldingTilBrukerNg2}, for bruk ved konstruering av {@link FeilhåndteringArgumenter} instans. */
export function visFeilmeldingVhjaMeldingTilBruker(feilmelding: string, meldingTilBruker: MeldingTilBrukerNg2): visFeilmeldingFunk {
    return () => meldingTilBruker.feilmelding(feilmelding);
}


/** Angir hvorvidt verdi skal leveres til abonnenter dersom entitet slettes i lokalt datalager.
 * Kun aktuelt for datalager-spørringer basert på id, feks elf selectEntity(<id>) */
export enum Leveringsvalg {
    /** Dersom entitet slettes i lokalt datalager vil 'undefined' bli levert til abonnenter */
    lever_undefined_ved_slettet,
    /** Dersom entitet slettes i lokalt datalager vil ingen verdi bli levert til abonnenter */
    ikke_lever_ved_slettet
}
